import {
  AccountTypeEnum,
  LocationType,
  PaymentTypeEnum,
  PowerTypeEnum,
  ReceiptStatusEnum,
  SourceTypeEnum,
  UserRole,
  VehicleTypeEnum,
} from './enums';

export type Facility = {
  name: string;
  iconUrl: string;
};

export type OpenHoursEntry = {
  day: string;
  open: string;
  close: string;
};

export type OpenHours = {
  entries: OpenHoursEntry[];
};

export type LocationConstants = {
  waterTariff: number;
  powerTariff: number;
  taxRate: number;
  openHours: OpenHours;
  portDepthRange: string;
  maxSocketSize: string;
  waterSocketSize: string;
  maxVehicleLength: number;
  maxVehicleBeamLength: number;
  berthCount: number;
  defaultPaymentType?: PaymentTypeEnum;
};

export type LocationWorkingHours = {
  dayOfTheWeek: number;
  openingTime: number;
  closingTime: OpenHours;
};

export type Location = {
  id: number;
  type: LocationType;
  name: string;
  description: string;
  country: string;
  city: string;
  address: string;
  postcode: string;
  logoUrl: string;
  imageUrl: string;
  thumbnailUrl: string;
  websiteUrl: string;
  phone: string;
  email: string;
  latitude: number;
  longitude: number;
  shopId: string;
  /** Payment gateway for this location (WSPAY | WEBPAY | STRIPE | MANUAL). */
  paymentProvider?: string;
  /**
   * Server-computed, present on /users/me account locations and the client
   * /locations routes: the location has a working card gateway, so add funds
   * may be offered (test webshops like TSTMAREX count only outside
   * production).
   */
  hasCardPaymentEnabled?: boolean;
  pedestals: Pedestal[];
  facilities: Facility[];
  constants: LocationConstants;
  workingHours: LocationWorkingHours[];
  parentLocation?: Location;
  subLocations?: Location[];
  isMasterLocation?: boolean;
};

export type Vehicle = {
  id: number;
  length?: number;
  registration: string;
  type: VehicleTypeEnum;
};

export type User = {
  username?: string;
  id: number;
  email?: string;
  name: string;
  password: string;
  phoneNumber: string | null;
  country: string | null;
  funds: number;
  vehicles: Vehicle[];
  accounts: Account[];
  charter?: Charter;
  ownedCharter?: Charter;
  role: UserRole;
  verified: boolean;
  r1CompanyName?: string;
  r1CompanyAddress?: string;
  r1CompanyCity?: string;
  r1CompanyCountry?: string;
  r1CompanyZipCode?: string;
  r1VatNumber?: string;
};

export interface Charter {
  id: number;
  owner: User;
  employees?: User[];
  dedicatedPedestals?: Pedestal[];
  deletedAt?: Date;
  createdAt?: Date;
  updatedAt?: Date;
}

export type Token = {
  token: string;
};

export type SourceState = {
  activated: boolean;
  modbusStable: boolean;
  fusesStable: boolean;
  socketStable: boolean;
  value: number;
};

export type Valve = {
  id: number;
  position: number;
  type: SourceTypeEnum;
  connectionSize: string;
  pedestal: Pedestal;
  state: SourceState;
  status: SourceStatusEnum;
  activeSession: Session;
};

export enum SourceStatusEnum {
  AVAILABLE = 'AVAILABLE',
  IN_USE = 'IN_USE',
  PLUGGED_IN = 'PLUGGED_IN',
  OUT_OF_SERVICE = 'OUT_OF_SERVICE',
  MANUAL = 'MANUAL',
}

export type Socket = {
  id: number;
  position: number;
  type: SourceTypeEnum;
  phase: PowerTypeEnum;
  current: number;
  pedestal: Pedestal;
  state: SourceState;
  status: SourceStatusEnum;
  activeSession: Session;
};

export type Source = Valve | Socket;

export type Pedestal = {
  id: string;
  identifier: string;
  location: Location;
  valves: Valve[];
  sockets: Socket[];
};

export type Session = {
  group: SessionGroup;
  source: Valve | Socket;
  vehicle: Vehicle;
  createdAt: Date;
  endedAt: Date;
  tariff: number;
  initialMeterState: number;
  id?: number;
};

export enum SessionRequestStatusEnum {
  APPROVED = 'APPROVED',
  DENIED = 'DENIED',
  PENDING = 'PENDING',
}

export enum SessionRequestActionEnum {
  ON = 'ON',
  OFF = 'OFF',
}

export type SessionRequest = {
  id: number;
  account: Account;
  pedestal: Pedestal;
  source: Source;
  vehicle: Vehicle;
  activatedBy: User;
  status: SessionRequestStatusEnum;
  action: SessionRequestActionEnum;
};

export type SessionGroup = {
  id: number;
  account: Account;
  location: Location;
  sessions: Session[];
  createdAt: string;
  endedAt: string;
};

export type PaymentCheckout =
  | {
      flow: 'FORM_POST';
      actionUrl: string | null;
      fields: Record<string, string>;
    }
  | {
      flow: 'CLIENT_COMPONENT';
      provider: string;
      params: Record<string, string>;
    }
  | { flow: 'REDIRECT'; url: string };

export type ActiveAuthorization = {
  id: number;
  reservedAmount: number;
  consumedAmount: number;
  status: string;
  createdAt: Date;
};

export type ActiveAuthorizationResponse = {
  preauthEnabled: boolean;
  authorization: ActiveAuthorization | null;
};

/**
 * Saved cards at a location. `enabled` is false when the gateway does not have
 * tokenization switched on for that merchant — the UI then hides the whole
 * saved-card section instead of promising something that would not happen.
 */
export type SavedCardsResponse = {
  enabled: boolean;
  cards: SavedCard[];
};

/** A card the user kept for next time; only the gateway holds the real number. */
export type SavedCard = {
  id: number;
  maskedPan?: string;
  cardType?: string;
  expirationDate?: string;
  createdAt: string;
};

export type PaymentOrder = {
  id: number;
  provider?: string;
  transactionType?: string;
  reservedAmount?: number;
  shopId: string;
  createdAt: Date;
  amount: string;
  version: string;
  signature: string;
  returnURL: string;
  returnErrorURL: string;
  cancelURL: string;
  invoiceId?: string;
  invoiceDate?: Date;
  isR1Invoice?: boolean;
  /** Present on create-order responses: how to hand the buyer to the gateway. */
  checkout?: PaymentCheckout;
};

interface AccountTariffs {
  [locationId: number]: {
    paymentType: string;
    powerTariff: number;
    waterTariff: number;
    numberOfDaysUntilPayment: number;
  };
}

export type Account = {
  id: number;
  balance: number;
  type: AccountTypeEnum;
  paymentType: PaymentTypeEnum;
  location: Location;
  waterTariff?: number;
  powerTariff?: number;
  specialTariffs?: AccountTariffs;
};

export type ReceiptItem = {
  consumption: number;
  createdAt: Date;
  deletedAt: Date;
  endDate: Date;
  id: number;
  service: string;
  pedestalIdentifier: string;
  phaseType?: PowerTypeEnum;
  sourceType: SourceTypeEnum;
  startDate: Date;
  tariff: number;
  updatedAt: Date;
  vehicleRegistration: string;
  vehicleType: string;
  pedestal?: Pedestal;
};

export type Receipt = {
  id: number;
  transactionId: string;
  account: Account;
  items: ReceiptItem[];
  periodStartDate: Date;
  periodEndDate: Date;
  status: ReceiptStatusEnum;
  paymentType: PaymentTypeEnum;
  paidAt: Date;
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date;
};
